import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthenticationService } from "../../../auth/service";
import { CoreConfigService } from "@core/services/config.service";
import { ApplicationService } from "@core/services/application.service";
import { Theme } from "@core/types/app/theme";
import { AnalyticsService } from "../../../services/analytics.service";

declare var zE: any;

@Component({
  selector: 'app-business-registration',
  templateUrl: './business-registration.component.html',
  styleUrls: ['./business-registration.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BusinessRegistrationComponent implements OnInit, OnDestroy {
  isSuccessful: boolean = false;
  isAlreadyRegistered: boolean = true;
  isValid: boolean = true;
  showErrorModal = false;
  showMessenger: boolean = false;
  public businessRegistration: FormGroup;
  public coreConfig: any;

  // Private
  private _unsubscribeAll: Subject<any>;
  private location: any;
  private address: any;

  business = {
    name: '',
    vatNumber: '',
    fiscalCode: '',
    uniqueCode: '',
    legalAddress: {
      street: '',
      number: '',
      zipCode: '',
      city: '',
      province: '',
      region: ''
    },
    shippingAddress: {
      street: '',
      number: '',
      zipCode: '',
      city: '',
      province: '',
      region: ''
    }
  };
  sameAddress: boolean = true;

  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: FormBuilder,
    private _authenticationService: AuthenticationService,
    private _applicationService: ApplicationService,
    private router: Router,
    private analyticsService: AnalyticsService // Inject AnalyticsService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: { hidden: true },
        menu: { hidden: true },
        footer: { hidden: true },
        customizer: false,
        enableLocalStorage: false
      }
    };
  }

  ngOnInit(): void {
    this.analyticsService.trackEvent('business_registration_page_viewed', 'Business registration page viewed', 'view');


    this.businessRegistration = this._formBuilder.group({
      name: ['', Validators.required],
      vatNumber: ['', Validators.required],
      fiscalCode: ['', Validators.required],
      pec: ['', Validators.required],
      uniqueCode: ['', Validators.required],
      legalStreet: ['', Validators.required],
      legalNumber: ['', Validators.required],
      legalZipCode: ['', Validators.required],
      legalCity: ['', Validators.required],
      legalProvince: ['', Validators.required],
      legalRegion: ['', Validators.required],
      Street: [''],
      Number: [''],
      ZipCode: [''],
      City: [''],
      Province: [''],
      Region: [''],
    });

    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
    });
  }

  onSubmit() {
    const formData = this.businessRegistration.value;
    this.location = {
      number: formData.legalNumber.trim(),
      street: formData.legalStreet.trim(),
      zip: formData.legalZipCode.trim(),
      city: formData.legalCity.trim(),
      province: formData.legalProvince.trim(),
      region: formData.legalRegion.trim(),
      country: 'IT'
    };

    if (this.sameAddress) {
      this.address = { ...this.location };
    } else {
      this.address = {
        number: formData.Number.trim(),
        street: formData.Street.trim(),
        zip: formData.ZipCode.trim(),
        city: formData.City.trim(),
        province: formData.Province.trim(),
        region: formData.Region.trim(),
        country: 'IT'
      }
    }

    formData.vatNumber = formData.vatNumber.trim().toUpperCase();
    if (formData.vatNumber.length == 11) {
      formData.vatNumber = 'IT' + formData.vatNumber;
    }

    if (!formData.vatNumber.trim() && !formData.fiscalCode.trim()) {
      const eventDetails = 'Registration error - Missing VAT or Fiscal Code';
      this.analyticsService.trackEvent('registration_error', eventDetails, 'error');


      Swal.fire({
        title: 'P.IVA o Codice Fiscale mancanti',
        icon: 'error',
        customClass: { confirmButton: 'btn btn-primary' }
      });
      return;
    }

    if (!this.location.zip.trim() || !this.address.zip.trim()) {
      const eventDetails = 'Registration error - Missing zip code';
      this.analyticsService.trackEvent('registration_error', eventDetails, 'error');


      Swal.fire({
        title: 'CAP mancante',
        icon: 'error',
        customClass: { confirmButton: 'btn btn-primary' }
      });
      return;
    }

    this._authenticationService.businessRegistration(
      formData.name.trim(),
      formData.vatNumber.trim(),
      formData.fiscalCode.trim(),
      formData.pec.trim(),
      formData.uniqueCode.trim(),
      this.location,
      this.address
    ).then(() => {
      const eventDetails = `Business registered - Name: ${formData.name.trim()}, VAT Number: ${formData.vatNumber.trim()}`;
      this.analyticsService.trackEvent('business_registered', eventDetails, 'registration');


      this._authenticationService.fetchBusinesses();
      Swal.fire({
        title: 'Azienda registrata correttamente',
        icon: 'success',
        customClass: { confirmButton: 'btn btn-primary' }
      }).then(() => {
        this.router.navigate(['/authentication/business-selection']);
      });
    }).catch((httpError) => {
      const { error } = httpError;

      if (error.type === "VALIDATION_ERROR") {
        let text = '';
        (error.data || []).forEach(error => {
          switch (error.field) {
            case 'name':
              text += 'Nome non valido</br>';
              break;
            case 'vatNumber':
              text += 'P.IVA non valida</br>';
              break;
            case 'fiscalCode':
              text += 'Codice Fiscale non valido</br>';
              break;
            case 'pec':
              text += 'PEC non valida</br>';
              break;
            case 'uniqueCode':
              text += 'Codice Unico non valido</br>';
              break;
            case 'location.street':
            case 'location.number':
              text += 'Indirizzo legale non valido</br>';
              break;
            case 'location.zip':
              text += 'CAP legale non valido</br>';
              break;
            case 'location.city':
              text += 'Città legale non valida</br>';
              break;
            case 'location.province':
              text += 'Provincia legale non valida</br>';
              break;
            case 'location.region':
              text += 'Regione legale non valida</br>';
              break;
            case 'address.street':
            case 'address.number':
              if (!this.sameAddress) {
                text += 'Indirizzo di consegna non valido</br>';
              }
              break;
            case 'address.zip':
              if (!this.sameAddress) {
                text += 'CAP di consegna non valido</br>';
              }
              break;
            case 'address.city':
              if (!this.sameAddress) {
                text += 'Città di consegna non valida</br>';
              }
              break;
            case 'address.province':
              if (!this.sameAddress) {
                text += 'Provincia di consegna non valida</br>';
              }
              break;
            case 'address.region':
              if (!this.sameAddress) {
                text += 'Regione di consegna non valida</br>';
              }
              break;
            default:
              break;
          }
        });

        const eventDetails = `Validation error - ${text}`;
        this.analyticsService.trackEvent('registration_error', eventDetails, 'error');


        Swal.fire({
          title: 'Errore nella registrazione',
          html: text,
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' }
        });
      } else if (error.name) {
        let errorMessage = '';
        if (error.message === "Missing pec or uniqueCode") {
          errorMessage = 'Pec o Codice Unico mancanti';
        } else if (error.message.startsWith("Invalid VAT number")) {
          errorMessage = 'P.IVA non valida';
        }

        const eventDetails = `Registration error - ${errorMessage}`;
        this.analyticsService.trackEvent('registration_error', eventDetails, 'error');


        Swal.fire({
          title: 'Errore nella registrazione',
          html: errorMessage,
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' }
        });
      } else if(error === 'Business already registered') {
        this.analyticsService.trackEvent('registration_error', 'Registration error - Business already registered', 'error');

        Swal.fire({
          title: 'Azienda già registrata',
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' }
        });
      } else {
        this.analyticsService.trackEvent('registration_error', 'Registration error - System error', 'error');


        Swal.fire({
          title: 'Errore nel sistema',
          html: 'Il sistema non è al momento disponibile. Riprova più tardi o contatta l\'assistenza tramite il pulsante di aiuto sotto oppure alla mail info@tulero.it.',
          icon: 'error',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        })
      }
    });
  }


  isFormValid(): boolean {
    // Implement your validation logic here
    // You can check each form field and their conditions
    // For instance, you can check if the 'name' field is empty:
    return this.business.name.trim() !== '';
  }

  onCheckboxChange() {
    this.sameAddress = !this.sameAddress;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

}
